import { useState } from 'react'
import { differenceInCalendarDays } from 'date-fns'
import Loading from '@/components/Loading'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { usePlatformOwnersQuery } from '@/hooks/usePlatformOwners'
import { usePaginationCursor } from '@/hooks/usePaginationCursor'
import { useNavigate } from 'react-router-dom'
import { UnsuspendOwnerModal } from './ui/UnsuspendOwnerModal'
import { SuspendOwnerModal } from './ui/SuspendOwnerModal'
import { isBillingExpired, resolveOwnerStatus } from '@/lib/ownerStatus'
import { DisabledWithTooltip } from '@/components/DisabledWithTooltip'
import { resolveMessagingStatus } from '@/lib/messagingStatus'

const daysSinceCreation = (createdAt?: string) =>
  createdAt ? differenceInCalendarDays(new Date(), new Date(createdAt)) : null

const bytesToHuman = (s: string) => {
  const n = Number(s)
  if (!Number.isFinite(n)) return s
  const kb = 1024
  const mb = kb * 1024
  const gb = mb * 1024
  if (n >= gb) return `${(n / gb).toFixed(2)} GB`
  if (n >= mb) return `${(n / mb).toFixed(2)} MB`
  if (n >= kb) return `${(n / kb).toFixed(2)} KB`
  return `${n} B`
}

export default function PlatformOwnersPage() {
  const navigate = useNavigate()

  const take = 30
  const { cursor, cursorStack, goNext: goNextCursor, goPrev, reset } = usePaginationCursor()
  const [includeDeleted, setIncludeDeleted] = useState(false)

  const { data, isPending, error, isFetching } = usePlatformOwnersQuery({
    take,
    cursor,
    includeDeleted,
  })

  if (isPending) return <Loading />
  if (error || !data?.ok)
    return (
      <div className='text-sm text-destructive'>Erreur chargement owners</div>
    )

  const hasMore = Boolean(data.page?.hasMore)
  const nextCursor = data.page?.nextCursor ?? null

  const goNext = () => goNextCursor(nextCursor)

  return (
    <Card>
      <CardHeader>
        <div className='flex items-start justify-between gap-3'>
          <div>
            <h2 className='text-xl font-semibold'>Owners</h2>
          </div>

          <div className='flex items-center gap-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={goPrev}
              disabled={cursorStack.length === 0 || isFetching}
            >
              Précédent
            </Button>

            <Button
              variant='outline'
              size='sm'
              onClick={goNext}
              disabled={!hasMore || !nextCursor || isFetching}
            >
              Suivant
            </Button>

            <Button
              variant={includeDeleted ? 'default' : 'outline'}
              size='sm'
              onClick={() => {
                setIncludeDeleted((v) => !v)
                reset()
              }}
            >
              {includeDeleted ? 'Masquer les supprimés' : 'Afficher les supprimés'}
            </Button>

            <Button
              variant='ghost'
              size='sm'
              onClick={reset}
              disabled={(cursorStack.length === 0 && !cursor) || isFetching}
            >
              Reset
            </Button>
          </div>
        </div>

        {isFetching ? (
          <p className='mt-2 text-xs text-muted-foreground'>Chargement…</p>
        ) : null}
      </CardHeader>

      <CardContent>
        <div className='space-y-2'>
          {data.items.map((o) => {
            const status = resolveOwnerStatus(o)
            const billingExpired = isBillingExpired(o)

            // règles d’action
            const canSuspend = status === 'ACTIVE'
            const canUnsuspend = status === 'SUSPENDED' && !billingExpired

            const suspendReason = (() => {
              if (status === 'INACTIVE') return 'Owner inactif.'
              if (status === 'EXPIRED') return 'Plan expiré.'
              if (status === 'SUSPENDED') return 'Déjà suspendu.'
              return undefined
            })()

            const unsuspendReason = (() => {
              if (status !== 'SUSPENDED') return 'Owner non suspendu.'
              if (billingExpired) return 'Plan expiré: réactivation bloquée.'
              return undefined
            })()

            const messagingStatus = resolveMessagingStatus(o)

            return (
              <div
                key={o.id}
                className='grid grid-cols-1 gap-3 rounded-lg border p-3 md:grid-cols-[1fr_auto]'
              >
                <div>
                  <div className='flex items-center gap-2'>
                    <div className='font-medium'>{o.name}</div>
                    {o.deletedAt ? (
                      <span className='rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-medium text-destructive'>
                        Supprimé
                      </span>
                    ) : null}
                  </div>
                  <div className='text-xs text-muted-foreground'>{o.id}</div>
                  <div className='text-xs text-muted-foreground'>
                    {o.ownerEmail ?? '—'}
                  </div>
                  <div className='text-xs text-muted-foreground'>
                    Inscrit le {o.createdAt ? o.createdAt.slice(0, 10) : '—'}
                    {' • '}
                    {daysSinceCreation(o.createdAt) ?? '—'} j
                  </div>

                  <div className='mt-2 text-xs text-muted-foreground'>
                    subaccounts:{' '}
                    <span className='font-medium text-foreground'>
                      {o.subaccountsCount}
                    </span>{' '}
                    • storage:{' '}
                    <span className='font-medium text-foreground'>
                      {bytesToHuman(o.storage.bytesUsed)}
                    </span>
                  </div>
                </div>

                <div className='text-right'>
                  <div className='text-sm'>{o.plan}</div>

                  <div className='text-xs text-muted-foreground'>
                    {status} • {o.nextRenewalAt ?? '—'}
                  </div>

                  <div className='mt-1 text-[11px] text-muted-foreground'>
                    Messaging: {messagingStatus}
                  </div>

                  <div className='mt-2 text-xs space-y-1'>
                    <div>
                      SMS:{' '}
                      <span className='font-medium'>
                        {o.messageUsageByChannel?.SMS?.remaining ?? 0}/
                        {o.messageUsageByChannel?.SMS?.totalIncluded ?? 0}
                      </span>
                    </div>
                    <div>
                      EMAIL:{' '}
                      <span className='font-medium'>
                        {o.messageUsageByChannel?.EMAIL?.remaining ?? 0}/
                        {o.messageUsageByChannel?.EMAIL?.totalIncluded ?? 0}
                      </span>
                    </div>
                    <div>
                      WHATSAPP:{' '}
                      <span className='font-medium'>
                        {o.messageUsageByChannel?.WHATSAPP?.remaining ?? 0}/
                        {o.messageUsageByChannel?.WHATSAPP?.totalIncluded ?? 0}
                      </span>
                    </div>
                  </div>

                  {billingExpired ? (
                    <div className='mt-1 text-[11px] text-muted-foreground'>
                      Billing: EXPIRED
                    </div>
                  ) : null}
                </div>

                <div className='flex flex-col items-end gap-2 md:flex-row md:items-start'>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => navigate(`/platform/owners/${o.id}`)}
                  >
                    Voir
                  </Button>

                  {o.suspendedAt ? (
                    <DisabledWithTooltip
                      disabled={!canUnsuspend || isFetching}
                      reason={
                        isFetching ? 'Chargement en cours…' : unsuspendReason
                      }
                    >
                      <UnsuspendOwnerModal
                        ownerId={o.id}
                        ownerName={o.name}
                        disabled={!canUnsuspend || isFetching}
                      />
                    </DisabledWithTooltip>
                  ) : (
                    <DisabledWithTooltip
                      disabled={!canSuspend || isFetching}
                      reason={
                        isFetching ? 'Chargement en cours…' : suspendReason
                      }
                    >
                      <SuspendOwnerModal
                        ownerId={o.id}
                        ownerName={o.name}
                        disabled={!canSuspend || isFetching}
                      />
                    </DisabledWithTooltip>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        <div className='mt-4 flex items-center justify-between'>
          <div className='text-xs text-muted-foreground'>
            Page: {cursorStack.length + 1} • items: {data.items.length}
          </div>

          <div className='flex gap-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={goPrev}
              disabled={cursorStack.length === 0 || isFetching}
            >
              Précédent
            </Button>
            <Button
              variant='outline'
              size='sm'
              onClick={goNext}
              disabled={!hasMore || !nextCursor || isFetching}
            >
              Suivant
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
